import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../../../environments/environment';

export interface StripeStatusDto {
  connected: boolean;
  externalAccountId: string | null;
  status: 'pending' | 'active' | 'restricted' | 'disconnected';
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  connectedAt: string | null;
}

export interface StripeConnectResponseDto {
  onboardingUrl: string;
  externalAccountId: string;
  status: string;
}

export interface StripeLoginLinkDto {
  url: string;
}

export interface PaymentSettingsDto {
  acceptOnlinePayment: boolean;
  onlinePaymentRequired: boolean;
  acceptCashPayment: boolean;
  cancellationPolicy: string | null;
}

interface SingleResponse<T> {
  isSuccess: boolean;
  value: T;
}

@Injectable()
export class LocationPaymentsService {
  private readonly baseUrl = `${environment.apiUrl}/api/v2/booking/merchant-locations`;

  constructor(private readonly http: HttpClient) { }

  /**
   * Starts (or resumes) Stripe Connect onboarding for the location.
   * Returns the one-time onboarding URL the merchant must visit.
   */
  connectStripe(
    locationId: string,
    payload: { returnUrl?: string; refreshUrl?: string } = {}
  ): Observable<StripeConnectResponseDto> {
    return this.http
      .post<SingleResponse<StripeConnectResponseDto>>(
        `${this.baseUrl}/${locationId}/payments/stripe/connect`,
        payload
      )
      .pipe(map(r => r.value));
  }

  /**
   * Refreshes the Stripe account state (typically called when the merchant
   * returns from hosted onboarding).
   */
  refreshStripeStatus(locationId: string): Observable<StripeStatusDto> {
    return this.http
      .get<SingleResponse<StripeStatusDto>>(
        `${this.baseUrl}/${locationId}/payments/stripe/callback`
      )
      .pipe(map(r => r.value));
  }

  /** Reads the current Stripe Connect status (no Stripe API call). */
  getStripeStatus(locationId: string): Observable<StripeStatusDto> {
    return this.http
      .get<SingleResponse<StripeStatusDto>>(
        `${this.baseUrl}/${locationId}/payments/stripe/status`
      )
      .pipe(map(r => r.value));
  }

  /** Generates a one-time login link for the Stripe Express dashboard. */
  getStripeLoginLink(locationId: string): Observable<StripeLoginLinkDto> {
    return this.http
      .post<SingleResponse<StripeLoginLinkDto>>(
        `${this.baseUrl}/${locationId}/payments/stripe/login-link`,
        {}
      )
      .pipe(map(r => r.value));
  }

  /** Logically disconnects Stripe (the account is preserved at Stripe). */
  disconnectStripe(locationId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/${locationId}/payments/stripe/disconnect`
    );
  }

  /** Reads the location payment policy (which methods are accepted, etc.). */
  getPaymentSettings(locationId: string): Observable<PaymentSettingsDto> {
    return this.http
      .get<SingleResponse<PaymentSettingsDto>>(
        `${this.baseUrl}/${locationId}/payment-settings`
      )
      .pipe(map(r => r.value));
  }

  /** Updates the location payment policy. */
  updatePaymentSettings(
    locationId: string,
    payload: PaymentSettingsDto
  ): Observable<PaymentSettingsDto> {
    return this.http
      .put<SingleResponse<PaymentSettingsDto>>(
        `${this.baseUrl}/${locationId}/payment-settings`,
        payload
      )
      .pipe(map(r => r.value));
  }
}
